// src/pages/enterprise/EnterpriseTravelTab.js
//
// Per-org travel allocation (T5). Read-through-T6 (admin API) with the Firebase
// bearer, the same idiom as the sibling EnterpriseAgentAccessTab. Shows the funded
// earmark against in-transit / reserved / spent / remaining, a commitment progress
// bar, and lets a manager set the funded earmark.
//
//   GET  {T6_BASE}/api/admin/orgs/:orgId/travel-allocation
//   PUT  {T6_BASE}/api/admin/orgs/:orgId/travel-allocation   body { fundedUSD }
//
// Auth + base URL are reused verbatim from EnterpriseAgentAccessTab (Firebase ID
// token in an Authorization: Bearer header, REACT_APP_BACKEND_URL base).
//
// TODO[fast-follow]: surface a per-row "remaining allocation" figure on the
// directory page (src/pages/Enterprises.js). Not done here because that needs a
// BATCH endpoint (allocations for many orgs in one call); the per-org GET above
// would N+1 the list. Add e.g. GET {T6_BASE}/api/admin/orgs/travel-allocation
// (bulk) first, then a column keyed by org id.

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Divider,
    Button,
    TextField,
    LinearProgress,
    Skeleton,
    Alert,
    Snackbar,
    CircularProgress,
} from '@mui/material';
import { styled } from '@mui/system';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SaveIcon from '@mui/icons-material/Save';
import axios from 'axios';

import { T6_BASE, authHeader } from './EnterpriseAgentAccessTab';

// -----------------------------------------------------------------------------
//  STYLED  (matches EnterpriseFeesTab SectionCard visual language)
// -----------------------------------------------------------------------------

const SectionCard = styled(Card)({
    backgroundColor: '#111',
    border: '1px solid rgba(255,255,255,0.08)',
    color: '#fff',
    borderRadius: 14,
    padding: '0.8rem',
    marginBottom: '1.5rem',
});

const inputSx = {
    backgroundColor: '#161616',
    '& .MuiOutlinedInput-root': {
        color: '#fff',
        '& fieldset': { borderColor: '#333' },
        '&:hover fieldset': { borderColor: '#444' },
        '&.Mui-focused fieldset': { borderColor: '#12c099' },
    },
    '& input': { color: '#fff' },
};

const mintButtonSx = {
    backgroundColor: '#12c099',
    color: '#000',
    fontWeight: 700,
    textTransform: 'none',
    '&:hover': { backgroundColor: '#0ea884' },
};

// -----------------------------------------------------------------------------
//  HELPERS
// -----------------------------------------------------------------------------

const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const fmtUSD = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return '$0';
    return '$' + Math.round(n).toLocaleString('en-US');
};

// One stat cell in the strip. `accent` = mint (Remaining), `muted` = gray (In-transit).
const Stat = ({ label, value, accent, muted }) => (
    <Box sx={{ minWidth: 120 }}>
        <Typography sx={{ color: '#888', fontSize: '0.8rem' }}>{label}</Typography>
        <Typography
            sx={{
                fontWeight: 700,
                fontSize: '1.5rem',
                mt: 0.25,
                color: accent ? '#12c099' : muted ? '#aaa' : '#fff',
            }}
        >
            {value}
        </Typography>
    </Box>
);

// -----------------------------------------------------------------------------
//  COMPONENT
// -----------------------------------------------------------------------------

export default function EnterpriseTravelTab({ orgId }) {
    const [data, setData] = useState(null);        // null = not yet loaded
    const [loading, setLoading] = useState(true);  // true on first paint (never blank)
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [budgetInput, setBudgetInput] = useState('');
    const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });

    const showToast = (severity, message) => setToast({ open: true, severity, message });

    // ---------------- fetch ----------------
    const fetchAllocation = useCallback(async () => {
        if (!orgId) {
            setError('No organization selected. Open this page from the Enterprises list.');
            setData(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const headers = await authHeader();
            const res = await axios.get(`${T6_BASE}/api/admin/orgs/${orgId}/travel-allocation`, { headers });
            const alloc = res.data?.allocation ?? res.data ?? {};
            setData(alloc);
            // Seed the editable budget from the funded value on every (re)load.
            setBudgetInput(String(num(alloc.fundedUSD ?? alloc.funded)));
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to load travel allocation.');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [orgId]);

    useEffect(() => {
        fetchAllocation();
    }, [fetchAllocation]);

    // ---------------- save ----------------
    const handleSave = async () => {
        const fundedUSD = Number(budgetInput);
        if (!Number.isFinite(fundedUSD) || fundedUSD < 0) {
            showToast('error', 'Enter a funded budget of 0 or more.');
            return;
        }
        setSaving(true);
        try {
            const headers = await authHeader();
            await axios.put(
                `${T6_BASE}/api/admin/orgs/${orgId}/travel-allocation`,
                { fundedUSD },
                { headers }
            );
            showToast('success', 'Travel budget updated.');
            await fetchAllocation();
        } catch (err) {
            showToast('error', err.response?.data?.message || err.message || 'Failed to update travel budget.');
        } finally {
            setSaving(false);
        }
    };

    // ---------------- derived ----------------
    const funded = num(data?.fundedUSD ?? data?.funded);
    const inTransit = num(data?.inTransitUSD ?? data?.inTransit);
    const reserved = num(data?.reservedUSD ?? data?.reserved);
    const spent = num(data?.spentUSD ?? data?.spent);
    const remaining =
        data?.remainingUSD != null || data?.remaining != null
            ? num(data?.remainingUSD ?? data?.remaining)
            : funded - reserved - spent;
    const committed = reserved + spent;
    const committedPct = funded > 0 ? Math.min(100, Math.max(0, (committed / funded) * 100)) : 0;

    // ---------------- render ----------------
    return (
        <Box>
            {/* HEADER */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        Travel Allocation
                    </Typography>
                    <Typography sx={{ color: '#888', fontSize: '0.9rem', mt: 0.5, maxWidth: 620 }}>
                        Funded travel earmark for this org, and what has been reserved or spent against it.
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={<RefreshOutlinedIcon />}
                    onClick={fetchAllocation}
                    disabled={loading}
                    sx={{
                        borderColor: '#333',
                        color: '#fff',
                        textTransform: 'none',
                        flexShrink: 0,
                        '&:hover': { borderColor: '#12c099', color: '#12c099' },
                    }}
                >
                    Refresh
                </Button>
            </Box>

            {/* ERROR (Rule 3: surfaced, retryable, never a blank panel) */}
            {error && (
                <Alert
                    severity="error"
                    sx={{ bgcolor: 'rgba(255,107,107,0.1)', color: '#ff6b6b', border: '1px solid rgba(255,107,107,0.3)', mb: 2 }}
                    action={<Button color="inherit" size="small" onClick={fetchAllocation}>Retry</Button>}
                >
                    {error}
                </Alert>
            )}

            {/* LOADING */}
            {loading && !data && !error && (
                <SectionCard>
                    <CardContent>
                        <Skeleton variant="text" width="30%" sx={{ bgcolor: '#222' }} />
                        <Box sx={{ display: 'flex', gap: 4, mt: 2, flexWrap: 'wrap' }}>
                            {[0, 1, 2, 3, 4].map((i) => (
                                <Box key={i}>
                                    <Skeleton variant="text" width={70} sx={{ bgcolor: '#1a1a1a' }} />
                                    <Skeleton variant="text" width={90} height={34} sx={{ bgcolor: '#222' }} />
                                </Box>
                            ))}
                        </Box>
                        <Skeleton variant="rounded" height={10} sx={{ bgcolor: '#222', mt: 3, borderRadius: 5 }} />
                    </CardContent>
                </SectionCard>
            )}

            {/* CONTENT */}
            {data && !error && (
                <>
                    <SectionCard>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 1 }}>Allocation</Typography>
                            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 2 }} />

                            {/* stat strip */}
                            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                <Stat label="Funded" value={fmtUSD(funded)} />
                                <Stat label="In-transit" value={fmtUSD(inTransit)} muted />
                                <Stat label="Reserved" value={fmtUSD(reserved)} />
                                <Stat label="Spent" value={fmtUSD(spent)} />
                                <Stat label="Remaining" value={fmtUSD(remaining)} accent />
                            </Box>

                            {/* In-transit reads as not-yet-spendable */}
                            {inTransit > 0 && (
                                <Typography sx={{ color: '#888', fontSize: '0.85rem', mt: 1.5, fontStyle: 'italic' }}>
                                    {fmtUSD(inTransit)} funding settling, not yet spendable.
                                </Typography>
                            )}

                            {/* commitment progress: (reserved + spent) / funded */}
                            <Box sx={{ mt: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography sx={{ color: '#888', fontSize: '0.8rem' }}>
                                        Committed (reserved + spent) of funded
                                    </Typography>
                                    <Typography sx={{ color: '#888', fontSize: '0.8rem' }}>
                                        {fmtUSD(committed)} / {fmtUSD(funded)}
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={committedPct}
                                    sx={{
                                        height: 10,
                                        borderRadius: 5,
                                        backgroundColor: '#222',
                                        '& .MuiLinearProgress-bar': { backgroundColor: '#12c099' },
                                    }}
                                />
                            </Box>
                        </CardContent>
                    </SectionCard>

                    {/* set funded earmark */}
                    <SectionCard>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 1 }}>Set travel budget</Typography>
                            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 2 }} />

                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                                <Box sx={{ maxWidth: 260 }}>
                                    <Typography sx={{ color: '#aaa', mb: 0.5 }}>Funded earmark ($)</Typography>
                                    <TextField
                                        type="number"
                                        size="small"
                                        value={budgetInput}
                                        onChange={(e) => setBudgetInput(e.target.value)}
                                        inputProps={{ min: 0 }}
                                        sx={inputSx}
                                    />
                                    <Typography sx={{ color: '#666', fontSize: '0.8rem', mt: 0.5 }}>
                                        Total dollars earmarked for this org's travel. Must be 0 or more.
                                    </Typography>
                                </Box>
                                <Button
                                    variant="contained"
                                    startIcon={saving ? <CircularProgress size={16} sx={{ color: '#06281f' }} /> : <SaveIcon />}
                                    onClick={handleSave}
                                    disabled={saving}
                                    sx={{ ...mintButtonSx, mt: 3.5 }}
                                >
                                    {saving ? 'Saving…' : 'Save budget'}
                                </Button>
                            </Box>
                        </CardContent>
                    </SectionCard>
                </>
            )}

            <Snackbar
                open={toast.open}
                autoHideDuration={3500}
                onClose={() => setToast({ ...toast, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    severity={toast.severity}
                    onClose={() => setToast({ ...toast, open: false })}
                    sx={{ width: '100%' }}
                >
                    {toast.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
